import type { EdgeType, GraphEdge, GraphNode, LocalizedText, MythDataset, NodeType, RelationshipScope } from './data/schema'

export interface AtlasSemanticEdge {
  id: string
  kind: 'semantic'
  source: string
  target: string
  type: EdgeType
  directed: boolean
  label: LocalizedText
  featured: boolean
  relationshipScope: RelationshipScope
}

export interface AtlasRelationshipBundle {
  id: string
  kind: 'bundle'
  source: string
  target: string
  type: 'participation'
  directed: false
  label: LocalizedText
  featured: false
  relationshipScope: 'story'
  edgeIds: string[]
  storyIds: string[]
  count: number
}

export type AtlasDisplayEdge = AtlasSemanticEdge | AtlasRelationshipBundle

function belongsToStory(edge: GraphEdge, storyId: string) {
  return edge.storyRefs.includes(storyId)
    || (edge.contexts ?? []).some((context) => context.storyIds.includes(storyId))
}

export function toAtlasSemanticEdge(edge: GraphEdge): AtlasSemanticEdge {
  return {
    id: edge.id,
    kind: 'semantic',
    source: edge.source,
    target: edge.target,
    type: edge.type,
    directed: edge.directed,
    label: edge.label,
    featured: edge.featured,
    relationshipScope: edge.relationshipScope,
  }
}

function pairKey(source: string, target: string) {
  return [source, target].sort().join('::')
}

export function buildAtlasFocusProjection(
  dataset: Pick<MythDataset, 'edges'>,
  focusId: string,
  options: { currentStoryId?: string; selectedEdgeId?: string } = {},
): AtlasDisplayEdge[] {
  const directEdges = dataset.edges.filter((edge) => edge.source === focusId || edge.target === focusId)
  const directNodeIds = new Set([focusId])
  directEdges.forEach((edge) => {
    directNodeIds.add(edge.source)
    directNodeIds.add(edge.target)
  })
  const closureEdges = dataset.edges.filter((edge) => directNodeIds.has(edge.source) && directNodeIds.has(edge.target))
  const semanticEdges = closureEdges.filter((edge) => (
    edge.relationshipScope === 'stable'
    || edge.id === options.selectedEdgeId
    || Boolean(options.currentStoryId && belongsToStory(edge, options.currentStoryId))
  ))
  const bundleGroups = new Map<string, GraphEdge[]>()

  directEdges.forEach((edge) => {
    if (semanticEdges.some((candidate) => candidate.id === edge.id)) return
    const key = pairKey(edge.source, edge.target)
    const group = bundleGroups.get(key) ?? []
    group.push(edge)
    bundleGroups.set(key, group)
  })

  const bundles: AtlasRelationshipBundle[] = [...bundleGroups.entries()].map(([key, edges]) => {
    const [source, target] = key.split('::')
    const storyIds = [...new Set(edges.flatMap((edge) => [
      ...edge.storyRefs,
      ...(edge.contexts ?? []).flatMap((context) => context.storyIds),
    ]))].sort()
    const count = edges.length
    return {
      id: `bundle-${source}-${target}`,
      kind: 'bundle',
      source,
      target,
      type: 'participation',
      directed: false,
      label: {
        'zh-Hant': `${count} 項故事關係`,
        en: `${count} story ${count === 1 ? 'connection' : 'connections'}`,
      },
      featured: false,
      relationshipScope: 'story',
      edgeIds: edges.map((edge) => edge.id).sort(),
      storyIds,
      count,
    }
  })

  return [...semanticEdges.map(toAtlasSemanticEdge), ...bundles].sort((left, right) => left.id.localeCompare(right.id))
}

export function filterFocusedAtlasNodes(
  nodes: readonly GraphNode[],
  edges: readonly AtlasDisplayEdge[],
  focusId: string,
  selectedType?: NodeType,
  preservedNodeIds: ReadonlySet<string> = new Set(),
) {
  const directIds = new Set([focusId, ...preservedNodeIds])
  edges.forEach((edge) => {
    if (edge.source === focusId) directIds.add(edge.target)
    else if (edge.target === focusId) directIds.add(edge.source)
  })
  const visibleIds = new Set(nodes
    .filter((node) => directIds.has(node.id) && (
      node.id === focusId
      || preservedNodeIds.has(node.id)
      || !selectedType
      || node.type === selectedType
    ))
    .map((node) => node.id))
  const visibleNodes = nodes.filter((node) => visibleIds.has(node.id))
  const visibleEdges = edges.filter((edge) => visibleIds.has(edge.source) && visibleIds.has(edge.target))
  return { nodes: visibleNodes, edges: visibleEdges }
}
